import { useEffect, useRef } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';

import { sendTaggingEvent } from '../../../../../gtmClient';
import {
  logErrorWrapper,
  logWarnWrapper,
} from '../../../../../services/logWrapper';
import { REQUEST_HOTEL_DATE_PRICES_DATA } from '../../../helpers/constants/actionsConstants';
import * as constants from '../../../helpers/constants/dataTypeConstants';
import classifyHotelsDatePrices from '../../../helpers/data-parsing/classifyHotelsDatePrices';
import { useDateSelection } from '../../../helpers/hooks/useDateSelection';
import monthViewServiceQueryHelper from '../../../helpers/interface-handling/monthViewServiceQueryBuilder';
import { getBaseUrl } from '../../../selectors';

import {
  receiveHotelsDatePrices,
  receiveHotelsDatePricesEmpty,
  receiveHotelsDatePricesError,
} from './hotelPricingActions';

import type { RawHotelDatePricesResponse } from '../../../actions/types/dataTypes';
import type { State } from '../../../selectors/types';

const getIataCodes = (
  json: RawHotelDatePricesResponse,
): { originIata: string; destinationIata: string } | false => {
  if (!json.originIata) {
    return false;
  }
  return {
    originIata: json.originIata,
    destinationIata: json.destinationIata,
  };
};

const requestHotelDatePricesDataStarted = () => ({
  type: `${REQUEST_HOTEL_DATE_PRICES_DATA}_STARTED` as const,
});

export const useHotelPricing = () => {
  const dispatch = useDispatch();
  const store = useStore<State>();
  const baseUrl = useSelector(getBaseUrl);
  const { syncHotelDataOnBothDates } = useDateSelection();
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!baseUrl) {
      return;
    }

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    const state = store.getState();
    const url =
      monthViewServiceQueryHelper.buildHotelFlexDatesQueryString(state);

    dispatch(requestHotelDatePricesDataStarted());

    const doFetch = async () => {
      try {
        const response = await fetch(url);

        if (isStale()) return;

        if (!response.ok) {
          dispatch(
            receiveHotelsDatePricesError({
              error: constants.HOTELS_DATE_PRICES_OTHER_ERROR,
            }),
          );
          logWarnWrapper({
            message: 'requestHotelsDatePrices error',
            component: 'useHotelPricing',
            status: response.status,
            url,
          });
          syncHotelDataOnBothDates();
          return;
        }

        const json = await response.json();

        if (isStale()) return;

        const iataCodes = getIataCodes(json);
        try {
          sendTaggingEvent(iataCodes || {});
        } catch (taggingError) {
          logWarnWrapper({
            message: 'sendTaggingEvent error',
            component: 'useHotelPricing',
          });
        }

        const parsedResults = classifyHotelsDatePrices(json);
        if (Object.entries(parsedResults).length) {
          dispatch(receiveHotelsDatePrices(parsedResults));
        } else {
          dispatch(receiveHotelsDatePricesEmpty(parsedResults));
        }

        syncHotelDataOnBothDates();
      } catch (error) {
        if (isStale()) return;

        dispatch(
          receiveHotelsDatePricesError({
            error: constants.HOTELS_DATE_PRICES_NETWORK_ERROR,
          }),
        );

        logErrorWrapper(error as Error, {
          message: 'requestHotelsDatePrices error',
          component: 'useHotelPricing',
          url,
        });
      }
    };

    doFetch();
  }, [baseUrl, syncHotelDataOnBothDates, dispatch, store]);
};
